import { useEffect, useRef, useState } from "react";
import { searchPilots } from "../services/pilotsService";
import { PilotRef } from "../types/pilots";

export default function PilotChooser({ onSelect, onClose }: { onSelect: (uid: string) => void; onClose: () => void }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const confirm = useRef<HTMLButtonElement>(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<PilotRef[]>([]);
  const [selected, setSelected] = useState<PilotRef | null>(null);
  const [status, setStatus] = useState("Enter part of your pilot name.");
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    dialog.current?.showModal(); input.current?.focus();
    return () => { opener?.focus(); };
  }, []);
  useEffect(() => { if (selected) confirm.current?.focus(); else input.current?.focus(); }, [selected]);
  useEffect(() => {
    let active = true;
    setResults([]);
    if (!query.trim()) { setStatus("Enter part of your pilot name."); return; }
    setStatus("Searching pilots…");
    const timer = window.setTimeout(() => {
      searchPilots(query).then(pilots => {
        if (!active) return;
        setResults(pilots);
        setStatus(pilots.length ? `${pilots.length} results. Select an entry to review it.${pilots.length === 10 ? " Refine your search if your pilot is not listed." : ""}` : "No pilots match this name.");
      }).catch(() => { if (active) setStatus("Pilot search failed. Please retry."); });
    }, 250);
    return () => { active = false; clearTimeout(timer); };
  }, [query, retry]);
  return <dialog ref={dialog} className="pilot-chooser" aria-labelledby="chooser-title" onCancel={onClose}>
    <div className="discovery-heading"><h2 id="chooser-title">Find your pilot</h2><button onClick={onClose} aria-label="Close pilot chooser">Close</button></div>
    <p>Select a public entry to personalise this browser. No sign-in required.</p>
    {selected ? <div className="discovery-leaf"><h3>{selected.name || "Unnamed pilot"}</h3><p className="pilot-uid">Pilot UID: {selected.uid}</p>
      <p>This selects a public pilot entry. It does not verify ownership or grant access.</p>
      <div className="discovery-actions"><button ref={confirm} onClick={() => onSelect(selected.uid)}>Pin this pilot on this browser</button>
        <button onClick={() => setSelected(null)}>Back to results</button><button onClick={onClose}>Cancel</button></div>
    </div> : <><label htmlFor="pilot-search">Pilot name</label><input ref={input} id="pilot-search" type="search" value={query} onChange={e => setQuery(e.target.value)} autoComplete="off" />
      <p role="status">{status}</p>{status.includes("failed") && <button onClick={() => setRetry(n => n + 1)}>Retry search</button>}
      <ul className="pilot-search-results">{results.map(pilot => <li key={pilot.uid}><button onClick={() => setSelected(pilot)}>
        <strong>{pilot.name || "Unnamed pilot"}</strong><span className="pilot-uid">UID: {pilot.uid}</span>
        <span>{pilot.achievement_count ?? 0} achievements · Last seen {pilot.last_seen_at ? new Date(pilot.last_seen_at).toLocaleDateString("en-AU", { timeZone: "Australia/Melbourne" }) : "unavailable"}</span>
      </button></li>)}</ul></>}
  </dialog>;
}
